import { Component, computed, inject, OnDestroy, output, signal } from '@angular/core';
import { ImgFallbackDirective } from '@shared/directives/img-fallback';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthStore } from '@core/auth/auth.store';
// import { LanguageSelector } from '@shared/components/language-selector/language-selector';
import { ModalService } from '@shared/components/modal/modal.service';
import { ProfileOptions } from '@shared/components/profile-options/profile-options';
import { ThemeToggle } from '@shared/components/theme-toggle/theme-toggle';
import { ProjectContextStore } from '@core/project-context/project-context.store';
import { environment } from '@environments/environment';
import { FALLBACK_IMAGE_SRC } from '@shared/global/globals';

@Component({
  selector: 'app-header',
  imports: [
    MatButtonModule,
    MatIconModule,
    ProfileOptions,
    ThemeToggle,
    TranslatePipe,
    // LanguageSelector,
    ImgFallbackDirective,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnDestroy {
  private readonly authStore = inject(AuthStore);
  private readonly projectContextStore = inject(ProjectContextStore);
  private readonly modal = inject(ModalService);
  private readonly baseUrl = environment.apiUrl;

  protected readonly FALLBACK_IMAGE_SRC = FALLBACK_IMAGE_SRC;
  protected readonly userEmail = computed(() => this.authStore.user()?.email ?? '');
  protected readonly username = computed(() => this.authStore.user()?.name ?? '');
  protected readonly toggleSidebarOutput = output();
  protected readonly isMobile = signal(window.innerWidth <= 768);
  protected readonly selectedProjectName = computed(() => {
    return this.projectContextStore.activeProject()?.name ?? ''
  });

  // TODO: Implement project logo when backend provides URL to access content. For now, we will use a placeholder image.
  protected readonly selectedProjectLogo = computed(() =>  {
    if (this.projectContextStore.activeProject()?.logo) {
      return `${this.baseUrl}Projects/Images/${this.projectContextStore.activeProject()?.logo}`;
    }
    return this.FALLBACK_IMAGE_SRC;
  });

  constructor() {
    window.addEventListener('resize', this.setIsMobile.bind(this));
  }

  setIsMobile() {
    this.isMobile.set(window.innerWidth <= 768);
  }

  /**
   * Signing out is one click away in a menu and expensive to undo — the token
   * is revoked and whatever the page held goes with it — so it asks first. The
   * store only hears about it once the user has said yes.
   */
  protected async onLogout(): Promise<void> {
    const confirmed = await this.modal.confirm({
      title: 'layout.top.logoutConfirm.title',
      message: 'layout.top.logoutConfirm.message',
      confirmText: 'layout.top.logout',
    });

    if (confirmed) {
      this.authStore.logout();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.setIsMobile.bind(this));
  }
}
